/*
 * Purpose: Compose timeline — NOW first, up next below, history fade strip
 * Last modified: 2026-06-28 | Agent
 * Completeness: 96
 */
import type { ReactNode } from 'react';
import { Check, Circle, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MissionStep } from '@/services/autoblocks/missionTypes';
import type { ComposeTimelineView } from '@/lib/composeTimeline';

interface ComposeTimelineRailProps {
  timeline: ComposeTimelineView;
  historySteps: MissionStep[];
  nowLabel?: string;
  children: ReactNode;
  className?: string;
}

export function ComposeTimelineRail({
  timeline,
  historySteps,
  nowLabel,
  children,
  className,
}: ComposeTimelineRailProps) {
  const { current, upcoming, completed, isComplete } = timeline;
  const showHistory = completed.length > 0 || historySteps.length > 0;

  return (
    <section className={cn('compose-timeline flex flex-col gap-0 w-full', className)} aria-label="Compose timeline">
      <div className="compose-now-zone relative scroll-mt-36 sm:scroll-mt-40" id="compose-now-zone" aria-label="Current step">
        <div className="flex items-center gap-2.5 mb-3 px-0.5">
          <span className="compose-now-badge inline-flex items-center rounded-full bg-primary px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-primary-foreground shadow-sm">
            Now
          </span>
          <span className="text-sm font-semibold text-foreground">
            {nowLabel ?? current.label}
          </span>
          {isComplete && (
            <span className="text-[10px] uppercase tracking-wide text-ab-green-deep dark:text-ab-green-glow font-medium ml-auto">
              Complete
            </span>
          )}
        </div>
        <div className="compose-now-content rounded-xl ring-2 ring-primary/35 ring-offset-2 ring-offset-background shadow-sm bg-card min-h-[12rem]">
          {children}
        </div>
      </div>

      {upcoming.length > 0 && (
        <div className="compose-upcoming mt-4 sm:mt-5" aria-label="Up next">
          <div className="flex items-center gap-2 mb-2.5 px-1">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/80">
              Up next
            </span>
            <div className="flex-1 h-px bg-border/60 border-dashed" aria-hidden />
          </div>
          <ol className="space-y-1.5">
            {upcoming.map((stage, i) => (
              <li
                key={stage.id}
                className={cn(
                  'compose-upcoming-item flex items-center gap-2.5 rounded-md border border-dashed border-muted-foreground/25',
                  'bg-muted/20 px-3 py-2 text-sm text-muted-foreground/75',
                  i === 0 && 'border-primary/20 bg-primary/[0.03]'
                )}
              >
                <Circle className="h-3.5 w-3.5 shrink-0 stroke-[1.5] opacity-50" aria-hidden />
                <span className="font-medium">{stage.label}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {showHistory && (
        <div className="compose-history mt-6 sm:mt-8 relative" aria-label="Session history">
          <div className="flex items-center gap-2 mb-2.5 px-1">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/50">
              History
            </span>
            <div className="flex-1 h-px bg-border/40" aria-hidden />
            <Lock className="h-3 w-3 text-muted-foreground/40" aria-hidden />
          </div>
          <div className="compose-history-mask relative max-h-[min(200px,28vh)] overflow-hidden rounded-lg border border-border/30 bg-muted/10">
            <ol className="compose-history-list p-3 space-y-2">
              {completed.map((stage) => (
                <li
                  key={stage.id}
                  className="compose-history-item flex items-start gap-2 text-xs text-muted-foreground/55"
                >
                  <Check className="h-3.5 w-3.5 shrink-0 mt-0.5 text-muted-foreground/45" aria-hidden />
                  <span className="font-medium line-through decoration-muted-foreground/30">{stage.label}</span>
                </li>
              ))}
              {historySteps.map((step) => (
                <li
                  key={step.id}
                  className="compose-history-item flex items-start gap-2 text-[11px] text-muted-foreground/45 pl-5"
                >
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-muted-foreground/35 shrink-0" aria-hidden />
                  <div className="min-w-0">
                    <p className="truncate">{step.label}</p>
                    {step.detail && (
                      <p className="truncate text-[10px] text-muted-foreground/35">{step.detail}</p>
                    )}
                  </div>
                </li>
              ))}
            </ol>
            <div
              className="compose-history-fade-strip pointer-events-none absolute inset-x-0 bottom-0 h-16"
              aria-hidden
            />
          </div>
        </div>
      )}
    </section>
  );
}
